<template>
  <div>
    <b-card class="mb-2">
      <div class="filters">
        <b-form-group
          label="Proyecto"
          class="filters__project mb-0"
        >
          <b-form-select
            v-model="filters.projectId"
            :options="projectOptions"
          />
        </b-form-group>

        <div class="filters__switches">
          <b-form-checkbox
            v-model="filters.unpaidOnly"
            switch
            @change="load"
          >
            Sólo no pagadas
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filters.onlyOverdue"
            switch
            @change="load"
          >
            Sólo vencidas
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filters.pendingRemission"
            switch
          >
            Sólo sin remisión
          </b-form-checkbox>
        </div>
      </div>
    </b-card>

    <b-card no-body>
      <b-table
        :items="visibleRows"
        :fields="fields"
        :busy="loading"
        responsive
        show-empty
        empty-text="No hay cuentas por cobrar con estos filtros."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center p-2">
            <b-spinner small class="mr-50" />
            Cargando…
          </div>
        </template>

        <template #cell(folio)="data">
          <b-button
            size="sm"
            variant="primary"
            class="font-weight-bolder"
            @click="goToPurchaseOrder(data.item)"
          >
            {{ data.value }}
          </b-button>
        </template>

        <template #cell(project)="data">
          {{ (data.value && data.value.name) || '—' }}
        </template>

        <template #cell(sale_date)="data">
          {{ data.value | date }}
        </template>

        <template #cell(due_date)="data">
          {{ data.value | date }}
        </template>

        <!-- Solo se puede subir mientras el envío no haya sido aprobado: una orden ya
             aprobada tiene su remisión validada y el backend rechaza otra. -->
        <template #cell(remission)="data">
          <div class="remission">
            <span
              :class="['remission__state', { 'remission__state--done': data.item.has_remission }]"
            >
              {{ remissionLabel(data.item) }}
            </span>
            <b-button
              v-if="canUpload(data.item)"
              size="sm"
              variant="outline-primary"
              :disabled="uploadingId === data.item.id"
              @click="pickFile(data.item)"
            >
              <b-spinner
                v-if="uploadingId === data.item.id"
                small
                class="mr-25"
              />
              Subir
            </b-button>
          </div>
        </template>

        <template #cell(invoice_number)="data">
          {{ data.value || '—' }}
        </template>

        <template #cell(amount)="data">
          $ {{ data.value | money }}
        </template>

        <template #cell(balance)="data">
          <span :class="{ 'text-danger font-weight-bold': Number(data.value) > 0 }">
            $ {{ data.value | money }}
          </span>
        </template>

        <template #cell(days_overdue)="data">
          <span :class="{ 'text-danger': Number(data.value) > 0 }">
            {{ data.value }}
          </span>
        </template>
      </b-table>
    </b-card>

    <!-- Un solo input para toda la tabla; se dispara desde el renglón que se eligió. -->
    <input
      ref="fileInput"
      type="file"
      class="d-none"
      multiple
      accept="image/*,application/pdf"
      @change="upload"
    >
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      loading: false,
      rows: [],
      uploadingId: null,
      pendingRow: null,
      filters: {
        projectId: null,
        unpaidOnly: true,
        onlyOverdue: false,
        // La razon principal por la que el proveedor entra aqui es ver que le falta subir.
        pendingRemission: false,
      },
      fields: [
        { key: 'folio', label: 'Folio', sortable: true },
        { key: 'project', label: 'Proyecto' },
        { key: 'sale_date', label: 'Compra', sortable: true },
        { key: 'due_date', label: 'Vencimiento', sortable: true },
        { key: 'remission', label: 'Remisión' },
        { key: 'invoice_number', label: 'Factura' },
        { key: 'amount', label: 'Importe', class: 'text-right' },
        { key: 'balance', label: 'Saldo', class: 'text-right' },
        { key: 'days_overdue', label: 'Días venc.', sortable: true },
      ],
    }
  },

  computed: {
    // Los proyectos salen de los propios renglones: son exactamente aquellos en los que el
    // proveedor tiene algo por cobrar, sin pedir un catálogo aparte.
    projectOptions() {
      const seen = new Map()
      // Sigue a los renglones que quedan tras los demas filtros: si el selector ofreciera
      // proyectos que ya no estan en la tabla, elegir uno dejaria la lista vacia.
      const base = this.filters.pendingRemission
        ? this.rows.filter(row => !row.has_remission)
        : this.rows

      base.forEach(({ project }) => {
        if (project?.id && !seen.has(project.id)) seen.set(project.id, project.name)
      })

      return [
        { value: null, text: 'Todos los proyectos' },
        ...Array.from(seen, ([value, text]) => ({ value, text })),
      ]
    },

    visibleRows() {
      return this.rows.filter(row => {
        if (this.filters.projectId && row.project?.id !== this.filters.projectId) return false
        if (this.filters.pendingRemission && row.has_remission) return false

        return true
      })
    },
  },

  watch: {
    // Si el proyecto elegido deja de estar en la lista al mover otro filtro, la tabla
    // quedaria vacia sin motivo visible. Se regresa a "todos".
    projectOptions(options) {
      const stillThere = options.some(option => option.value === this.filters.projectId)
      if (!stillThere) this.filters.projectId = null
    },
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('finance', ['fetchPayables']),
    ...mapActions('purchaseOrders', ['shipPurchaseOrder']),

    async load() {
      this.loading = true

      try {
        const data = await this.fetchPayables({
          unpaid_only: this.filters.unpaidOnly,
          only_overdue: this.filters.onlyOverdue,
        })
        this.rows = data?.data || []
      } catch (error) {
        this.toast('No se pudieron cargar las cuentas por cobrar.', 'danger')
      } finally {
        this.loading = false
      }
    },

    // El folio lleva a la orden de compra, que es la vista de esa cotizacion para el
    // proveedor. El renglon trae quote_id, no el id de la orden.
    goToPurchaseOrder(row) {
      if (!row?.quote_id) return

      this.$router.push(`/orden-de-compra/${row.quote_id}`)
    },

    remissionLabel(row) {
      if (!row.has_remission) return 'Pendiente'

      return row.remissions_count > 1 ? `${row.remissions_count} archivos` : 'Completa'
    },

    canUpload(row) {
      return row.shipping_status !== 'shipping_approved'
    },

    pickFile(row) {
      this.pendingRow = row
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    async upload(event) {
      const files = Array.from(event.target.files || [])
      const row = this.pendingRow
      if (!files.length || !row) return

      const formData = new FormData()
      files.forEach(file => formData.append('document_attachment[]', file, file.name))

      this.uploadingId = row.id

      try {
        await this.shipPurchaseOrder({ purchaseOrderId: row.id, attachments: formData })
        this.toast('Remisión enviada.', 'success')
        await this.load()
      } catch (error) {
        const message = error?.data?.messages?.[0] || 'No se pudo subir la remisión.'
        this.toast(message, 'danger')
      } finally {
        this.uploadingId = null
        this.pendingRow = null
      }
    },

    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'FileTextIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.filters__project {
  min-width: 18rem;
  flex: 1 1 18rem;
}

.filters__switches {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding-bottom: 0.35rem;
}

.remission {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.remission__state {
  font-size: 0.82rem;
  color: #b2aebf;
}

.remission__state--done {
  color: #1f9d63;
  font-weight: 600;
}
</style>
